
import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Payment } from '@/services/types';
import { StatusBadge } from '@/components/StatusBadge';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { fetchTableData } from '@/integrations/supabase/tableUtils';
import { Loader2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface PaymentHistoryProps {
  courierId: string;
}

export const PaymentHistoryTab: React.FC<PaymentHistoryProps> = ({ courierId }) => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        setLoading(true);
        console.log(`Получение истории платежей для курьера ${courierId}...`);
        
        // Получаем данные из Supabase
        const result = await fetchTableData('payments');
        
        if (result.success && result.data) {
          // Фильтруем только платежи нашего курьера
          const courierPayments = result.data
            .filter((payment: any) => payment.courier_id === courierId)
            .map((payment: any) => {
              return {
                id: payment.id,
                rentalId: payment.rental_id || 'rental-unknown',
                courierId: payment.courier_id,
                courierName: '',
                amount: payment.amount,
                date: payment.payment_date,
                status: payment.status || 'paid',
                method: payment.payment_method || 'card',
                human_number: payment.human_number, 
                rentalHumanNumber: payment.rental?.human_number || null,
              };
            });
          
          setPayments(courierPayments);
          console.log(`Загружено ${courierPayments.length} записей платежей для курьера`);
        } else {
          console.error('Ошибка при загрузке данных платежей:', result.error);
          // Показываем пустой массив вместо тестовых данных
          setPayments([]);
          setError('Не удалось загрузить данные из базы');
        }
      } catch (err) {
        console.error('Ошибка при загрузке истории платежей:', err);
        // Показываем пустой массив вместо тестовых данных
        setPayments([]);
        setError('Произошла ошибка при загрузке данных');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPayments();
  }, [courierId]);

  // Сортируем платежи по дате, от новых к старым
  const sortedPayments = [...payments].sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd.MM.yyyy HH:mm', { locale: ru });
    } catch (error) {
      console.error('Ошибка форматирования даты:', error, dateString);
      return 'Некорректная дата';
    }
  };

  const handleOpenInvoice = (paymentNumber: string | undefined) => {
    if (!paymentNumber) {
      return; // Ничего не делаем, если номера нет
    }
    
    const invoiceUrl = `https://sp1-nova.ru/api/pay/?subdomain=tuktukrent.amocrm.ru&invoice_id=${paymentNumber}`;
    window.open(invoiceUrl, '_blank');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>История платежей</CardTitle>
        <CardDescription>
          Все платежи клиента
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>Загрузка данных...</span>
          </div>
        ) : (
          <div className="space-y-4">
            {sortedPayments.length > 0 ? (
              sortedPayments.map((payment) => (
                <div key={payment.id} className="flex items-center justify-between border-b pb-2">
                  <div>
                    <div className="font-medium">
                      {payment.rentalHumanNumber ? (
                        <>
                          Оплата аренды №<span className="text-primary">{payment.rentalHumanNumber}</span>
                        </>
                      ) : payment.human_number ? (
                        <>
                          Оплата <span className="text-primary">{payment.human_number}</span>
                        </>
                      ) : (
                        <>Оплата за аренду №{payment.rentalId.substring(0, 8)}</>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {formatDate(payment.date)}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="font-medium">{payment.amount} ₽</div>
                      <div className="text-sm">
                        <StatusBadge status={payment.status} type="payment" />
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleOpenInvoice(payment.human_number)}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-muted-foreground py-8">
                {error ? error : 'Нет данных о платежах'}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
